import React, { useEffect, useState } from 'react';
import { Modal, Form, InputNumber, DatePicker, Checkbox, Button, message, Row, Col, Alert } from 'antd';
import { DatabaseOutlined } from '@ant-design/icons';
import { solarCalculation, getSolarDataRange } from '../../api/aiTools';
import { useTranslation } from 'react-i18next';
import './solarCalculationForm.scss';

const SolarCalculationForm = ({ visible, onCancel, onAfterSubmit, user }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [dataRange, setDataRange] = useState(null);

    useEffect(() => {
        if (visible && !dataRange) {
            getSolarDataRange().then((range) => {
                if (range) setDataRange(range);
            });
        }
    }, [visible, dataRange]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const formattedValues = {
                lat: values.lat,
                lon: values.lon,
                azimuth: values.azimuth ?? 0,
                slope: values.slope ?? 10,
                capacity_kw: values.capacity_kw ?? 1.0,
                start_date: values.start_date ? values.start_date.format('YYYY-MM-DD HH:mm') : null,
                end_date: values.end_date ? values.end_date.format('YYYY-MM-DD HH:mm') : null,
                return_hourly: values.return_hourly ?? false,
                return_daily: values.return_daily ?? true,
                return_monthly: values.return_monthly ?? true,
                return_stats: values.return_stats ?? true,
            };

            const result = await solarCalculation({ data: formattedValues });

            if (result.status === 200) {
                message.success(t('aiAssistant.solarCalculation.form.messages.submitSuccess'));
                onAfterSubmit(result);
                form.resetFields();
            } else {
                message.error(result.message || t('aiAssistant.solarCalculation.form.messages.submitFailed'));
            }
        } catch (error) {
            if (error.errorFields) {
                message.error(t('aiAssistant.solarCalculation.form.messages.formIncomplete'));
            } else {
                console.error('Solar calculation submit error:', error);
                message.error(t('aiAssistant.solarCalculation.form.messages.submitFailed'));
            }
        }
    };

    return (
        <Modal
            className="solar-calculation-modal"
            title={t('aiAssistant.solarCalculation.form.title')}
            open={visible}
            onCancel={onCancel}
            maskClosable={false}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    {t('aiAssistant.solarCalculation.form.buttons.cancel')}
                </Button>,
                <Button key="submit" type="primary" onClick={handleSubmit}>
                    {t('aiAssistant.solarCalculation.form.buttons.submit')}
                </Button>
            ]}
            centered
            width={700}
        >
            {dataRange && (
                <Alert
                    type="info"
                    showIcon
                    icon={<DatabaseOutlined />}
                    style={{ marginBottom: 16 }}
                    message={
                        <>
                            {t('aiAssistant.solarCalculation.form.dataRangeInfo', {
                                start: dataRange.data_start_local,
                                end: dataRange.data_end_local,
                            })}
                            <br />
                            {t('aiAssistant.solarCalculation.form.dataRangeCoverage', {
                                latMin: dataRange.lat_min,
                                latMax: dataRange.lat_max,
                                lonMin: dataRange.lon_min,
                                lonMax: dataRange.lon_max,
                            })}
                            <br />
                            {t('aiAssistant.solarCalculation.form.dataRangeStatsNote')}
                        </>
                    }
                />
            )}
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    lat: 22.42348,
                    lon: 120.52704,
                    azimuth: 0,
                    slope: 10,
                    capacity_kw: 1.0,
                    return_hourly: true,
                    return_daily: true,
                    return_monthly: true,
                    return_stats: true,
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label={t('aiAssistant.solarCalculation.form.fields.lat.label')}
                            name="lat"
                            rules={[{ required: true, message: t('aiAssistant.solarCalculation.form.fields.lat.required') }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={dataRange?.lat_min}
                                max={dataRange?.lat_max}
                                step={0.00001}
                                precision={5}
                                placeholder="22.42348"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label={t('aiAssistant.solarCalculation.form.fields.lon.label')}
                            name="lon"
                            rules={[{ required: true, message: t('aiAssistant.solarCalculation.form.fields.lon.required') }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={dataRange?.lon_min}
                                max={dataRange?.lon_max}
                                step={0.00001}
                                precision={5}
                                placeholder="120.52704"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            label={t('aiAssistant.solarCalculation.form.fields.azimuth.label')}
                            name="azimuth"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                max={360}
                                step={1}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label={t('aiAssistant.solarCalculation.form.fields.slope.label')}
                            name="slope"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                max={90}
                                step={1}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label={t('aiAssistant.solarCalculation.form.fields.capacity_kw.label')}
                            name="capacity_kw"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0.01}
                                step={0.1}
                                precision={2}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label={t('aiAssistant.solarCalculation.form.fields.start_date.label')}
                            name="start_date"
                            rules={[{ required: true, message: t('aiAssistant.solarCalculation.form.fields.start_date.required') }]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                showTime={{ format: 'HH:mm' }}
                                format="YYYY-MM-DD HH:mm"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label={t('aiAssistant.solarCalculation.form.fields.end_date.label')}
                            name="end_date"
                            rules={[{ required: true, message: t('aiAssistant.solarCalculation.form.fields.end_date.required') }]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                showTime={{ format: 'HH:mm' }}
                                format="YYYY-MM-DD HH:mm"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    label={t('aiAssistant.solarCalculation.form.fields.outputOptions.label')}
                >
                    <Row gutter={16}>
                        <Col>
                            <Form.Item name="return_hourly" valuePropName="checked" noStyle>
                                <Checkbox>{t('aiAssistant.solarCalculation.form.fields.outputOptions.hourly')}</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item name="return_daily" valuePropName="checked" noStyle>
                                <Checkbox>{t('aiAssistant.solarCalculation.form.fields.outputOptions.daily')}</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item name="return_monthly" valuePropName="checked" noStyle>
                                <Checkbox>{t('aiAssistant.solarCalculation.form.fields.outputOptions.monthly')}</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item name="return_stats" valuePropName="checked" noStyle>
                                <Checkbox>{t('aiAssistant.solarCalculation.form.fields.outputOptions.stats')}</Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default SolarCalculationForm;
